import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Button, 
  Input, 
  PasswordInput, 
  Checkbox, 
  Typography 
} from '../design-system';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [formError, setFormError] = useState('');
  const { login, register, isAuthenticated, loading, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redirect if user is already logged in
  useEffect(() => {
    if (isAuthenticated) {
      // Check if we should redirect to a specific note
      const savedNoteId = sessionStorage.getItem('redirectNoteId');
      if (savedNoteId) {
        // Clear the saved note ID
        sessionStorage.removeItem('redirectNoteId');
        // Navigate to the saved note
        navigate(`/notes/${savedNoteId}`, { replace: true });
      } else {
        // Otherwise redirect to dashboard or the originally requested page
        const from = location.state?.from?.pathname || '/';
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, navigate, location]);
  
  const validateForm = () => {
    setFormError('');
    
    if (!email) {
      setFormError('Email is required');
      return false;
    }
    
    if (!email.includes('@')) {
      setFormError('Please enter a valid email');
      return false;
    }
    
    if (!password) {
      setFormError('Password is required');
      return false;
    }
    
    if (isRegistering && password.length < 6) {
      setFormError('Password must be at least 6 characters');
      return false;
    }
    
    if (isRegistering && !name) {
      setFormError('Name is required');
      return false;
    }
    
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      if (isRegistering) {
        const success = await register({
          name,
          email,
          password,
          platformType: 'TASK_TRACKING'
        });
        
        if (success) {
          // Clear the form
          setEmail('');
          setPassword('');
          setName('');
          
          // Switch to login view
          setIsRegistering(false);
          
          // Show success message (using alert for now, replace with toast if available)
          alert('Registration successful! Please login.');
        }
      } else {
        await login(email, password);
        // Navigation will happen in the effect hook when isAuthenticated changes
      }
    } catch (err) {
      // Error handling is done in AuthContext
      console.error('Form submission error:', err);
    }
  };
  
  const toggleMode = () => {
    setIsRegistering(!isRegistering);
    setFormError('');
  };
  
  return (
    <div className="font-sans bg-gradient-to-br from-slate-50 via-white to-blue-50 min-h-screen">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl mb-4 shadow-glow animate-pulse-glow">
              <i className="fa-solid fa-brain text-white text-2xl"></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Organise<span className="text-primary-500">Wise</span>
            </h1>
            <p className="text-gray-600 mt-2">AI-powered organization platform</p>
          </div>

          {/* Login/Register Form */}
          <div className="bg-white rounded-2xl shadow-soft p-8 border border-gray-100 animate-slide-up">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {isRegistering ? 'Create your account' : 'Welcome back'}
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                {isRegistering ? 'Get started with OrganiseWise' : 'Sign in to your account'}
              </p>
            </div>

            {/* Google Login/Register Button */}
            <Button 
              variant="gray-outline" 
              size="lg" 
              className="w-full mb-4 shadow-sm hover:shadow-md"
              leftIcon={
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              }
            >
              Continue with Google
            </Button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">
                  or {isRegistering ? 'create with email' : 'continue with email'}
                </span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {isRegistering && (
                <Input
                  label="Name"
                  type="text"
                  placeholder="Enter your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  variant="login"
                  required
                />
              )}
              
              <Input
                label="Email address"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="login"
                required
              />
              
              <PasswordInput
                label="Password"
                placeholder={isRegistering ? "Create a password" : "Enter your password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="login"
                required
                helperText={isRegistering ? "Must be at least 8 characters" : undefined}
              />

              {!isRegistering && (
                <div className="flex items-center justify-between">
                  <Checkbox
                    label="Remember me"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  
                  <Typography 
                    variant="link" 
                    className="text-sm text-primary-500 hover:text-primary-600 transition cursor-pointer"
                  >
                    Forgot password?
                  </Typography>
                </div>
              )}

              {isRegistering && (
                <Checkbox
                  label={
                    <span className="text-sm text-gray-600">
                      I agree to the <span className="text-primary-500 hover:text-primary-600 cursor-pointer">Terms of Service</span> and <span className="text-primary-500 hover:text-primary-600 cursor-pointer">Privacy Policy</span>
                    </span>
                  }
                />
              )}

              {/* Error Message */}
              {(formError || error) && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <Typography color="error" className="text-sm">
                    {formError || error}
                  </Typography>
                </div>
              )}

              <Button 
                type="submit" 
                variant="primary" 
                size="lg" 
                className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-glow"
                loading={loading}
              >
                {isRegistering ? 'Create account' : 'Sign in'}
              </Button>
            </form>

            <div className="text-center mt-6">
              <Typography color="secondary" className="text-sm">
                {isRegistering ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button
                  type="button"
                  onClick={toggleMode}
                  className="text-primary-500 hover:text-primary-600 font-medium transition cursor-pointer"
                >
                  {isRegistering ? 'Sign in' : 'Sign up'}
                </button>
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen; 